import { useState, useEffect, memo } from "react";
import { useTranslation } from "react-i18next";
import { Button, Paper, Tab } from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
// cmps
import CenterCircularProgress from "./CenterCircularProgress";
import Toast from "./Toast";
// hooks
import useSend from "../hooks/useSend";
// services
import { getGupportHttpUrlFromGupportWsUrl } from "../services/serverOption";
import { downloadData } from "../services/terminal";
import { icons } from "@local/theme";
// types
import type { CmdCloudActionBiGetDashboards, BiDashboards as BiDashboardsT } from "../types/gupport";

const BiDashboards = () => {
	const send = useSend();
	const { t } = useTranslation();

	const [loading, setLoading] = useState(true);
	const [dashboards, setDashboards] = useState<BiDashboardsT>([]);
	const [selectedTab, setSelectedTab] = useState("");
	const [reportLoading, setReportLoading] = useState(false);
	const [showGenericErrorMsg, setShowGenericErrorMsg] = useState<number | undefined>(undefined);

	useEffect(() => {
		const cmd = {
			action: "cloudAction",
			module: "bi",
			function: "get_dashboards",
			params: [],
		} as const satisfies CmdCloudActionBiGetDashboards;
		send(cmd, (error, msg) => {
			if (!error && msg?.payload.status === "ok") {
				setDashboards(msg.payload.data);
				const firstDashboardId = msg.payload.data[0]?.id;
				if (firstDashboardId) {
					setSelectedTab(firstDashboardId);
				}
				setShowGenericErrorMsg(undefined);
			} else {
				setShowGenericErrorMsg(Date.now());
			}
			setLoading(false);
		});
	}, [send]);

	if (loading) {
		return <CenterCircularProgress />;
	}

	if (dashboards.length === 0) {
		return (
			<>
				<Paper component="section" style={{ padding: "16px" }}>{t("welcome.noDashboards")}</Paper>
				<Toast
					autoHideDuration={6000}
					severity="error"
					open={showGenericErrorMsg}
					onClose={setShowGenericErrorMsg}
					message={t("toast.genericErrorMsg")}
				/>
			</>
		);
	}

	const handleDownloadReport = async () => {
		setReportLoading(true);
		try {
			// Credentialed cross-origin fetch: the session cookie authenticates
			// through the oauth forwardAuth middleware (which requires an
			// Origin header, so a plain navigation would be rejected).
			const url = `${getGupportHttpUrlFromGupportWsUrl()}/firmware-report` as const;
			const response = await fetch(url, { credentials: "include", mode: "cors" });
			if (!response.ok) {
				throw new RangeError("Invalid HTTP-Statuscode", { cause: { status: response.status } });
			}
			downloadData("firmware-report-prod-latest.csv", "text/csv", await response.text());
		} catch (error) {
			console.error(error);
			setShowGenericErrorMsg(Date.now());
		} finally {
			setReportLoading(false);
		}
	};

	return (
		<div style={{ display: "flex", height: "calc(100vh - 160px)", gap: "12px" }}>
			<TabContext value={selectedTab}>
				<Paper component="nav" style={{ flexShrink: 0 }}>
					<TabList
						onChange={(_event, value: string) => (setSelectedTab(value))}
						orientation="vertical"
						variant="scrollable"
					>
						{dashboards.map((dashboard) => (
							<Tab
								key={dashboard.id}
								id={`tab-bi-${dashboard.id}`}
								label={dashboard.title}
								value={dashboard.id}
								style={{ textAlign: "left", alignItems: "flex-start" }}
							/>
						))}
					</TabList>
					<Button
						variant="contained"
						size="small"
						startIcon={<icons.Download />}
						onClick={handleDownloadReport}
						disabled={reportLoading}
						style={{ margin: "12px" }}
					>
						{t("welcome.downloadReport")}
					</Button>
				</Paper>
				{dashboards.map((dashboard) => (
					<TabPanel key={dashboard.id} value={dashboard.id} style={{ width: "100%" }}>
						<iframe
							src={dashboard.url}
							title={dashboard.title}
							width="100%"
							height="100%"
							loading="lazy"
							sandbox="allow-same-origin allow-scripts" // TODO: https://github.com/jsx-eslint/eslint-plugin-react/blob/master/docs/rules/iframe-missing-sandbox.md#rule-details
							style={{
								border: "none",
								flex: 1,
							}}
						/>
					</TabPanel>
				))}
			</TabContext>
			<Toast
				autoHideDuration={6000}
				severity="error"
				open={showGenericErrorMsg}
				onClose={setShowGenericErrorMsg}
				message={t("toast.genericErrorMsg")}
			/>
		</div>
	);
};

export default memo(BiDashboards);
